import { useMutation } from '@tanstack/react-query';
import { Col, DatePicker, Form, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { DefaultOptionType } from 'antd/es/select';
import { CustomerApis } from 'apis/CustomerApis';
import { PlantApi } from 'apis/PlantApis';
import { FormFilter, InputForm, SelectForm } from 'components';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { useUser } from 'contexts/UserProvider';
import dayjs from 'dayjs';
import Content from 'layouts/Content';
import { useCallback, useEffect, useState } from 'react';

const spanCol = 6;

export interface FilterTransactionFrequencyProps {
  defaultFilter?: any;
  onFilter?: (values: any) => void;
  onCancel?: () => void;
}

const FilterTransactionFrequency = ({
  defaultFilter,
  onCancel,
  onFilter,
}: FilterTransactionFrequencyProps) => {
  const { user } = useUser();
  const [form] = useForm();
  const [plants, setPlants] = useState<DefaultOptionType[]>([]);
  const [customersInRoute, setCustomersInRoute] =
    useState<ILazySelectData>(initLazySelectData);

  const { mutate: getCustomersMutate, isPending: isGetCustomersMutatePending } =
    useMutation({
      mutationFn: (params: {
        pageSize: number;
        searchValue: string;
        plantId: string;
      }) => CustomerApis.getCustomersInCombobox(params),
      onSuccess: (res) => {
        if (res?.data?.getCustomer) {
          const data = res?.data?.getCustomer;

          setCustomersInRoute({
            options: data.items.map(
              (item: any) =>
                ({
                  label:
                    (item?.sapCustomerId || item?.id) +
                    (item?.storeName ? ` - ${item.storeName}` : ''),
                  value: item?.id,
                }) as DefaultOptionType,
            ),
            totalItems: data?.pageInfo?.totalItems,
          });
        }
      },
    });

  const { mutate: getAllPlant, isPending: getAllPlantLoading } = useMutation({
    mutationFn: () => PlantApi.getAllPlantComboBox(),
    onSuccess: (data: any) => {
      const items = data.data?.getPlant?.items || [];

      if (!items?.length) return setPlants([]);

      setPlants(
        items.map((item: any) => ({
          label: item.id + (item.name ? ` - ${item.name}` : ''),
          value: item.id,
        })),
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const handleScrollCustomersChange = useCallback(
    (params: ILazySelectParams) => {
      const { pageSize, value } = params;
      getCustomersMutate({
        pageSize,
        plantId: '',
        searchValue: value ?? '',
      });
    },
    [getCustomersMutate],
  );

  const handleChangePlant = (value: any) => {
    form.setFieldsValue({
      teamSalesId: undefined,
      routeId: undefined,
      plantId: value,
    });
  };

  const handleFinish = (values: any) => {
    onFilter?.(values);
  };

  const handleCancel = () => {
    form.resetFields();

    form.setFieldsValue({
      plantId: defaultFilter?.plantId,
      year: dayjs(),
    });

    onCancel?.();
  };

  useEffect(() => {
    getAllPlant();
  }, [getAllPlant]);

  useEffect(() => {
    if (defaultFilter) {
      form.setFieldsValue({
        plantId: defaultFilter.plantId,
        year: defaultFilter.year,
      });
    }
  }, [defaultFilter.plantId, form]);

  return (
    <Content isOpen>
      <FormFilter
        okTitle="Xem báo cáo"
        form={form}
        onFinish={handleFinish}
        onCancel={handleCancel}
      >
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item
              label="Chi nhánh"
              name="plantId"
              // rules={[{ required: true, message: 'Vui lòng chọn chi nhánh' }]}
            >
              <SelectForm
                loading={getAllPlantLoading}
                options={plants}
                placeholder="Chọn chi nhánh"
                disabled={user?.plantCode !== '5000'}
                onChange={handleChangePlant}
              />
            </Form.Item>
          </Col>

          <Col span={8} className="h-[80px]">
            <Form.Item label="Khách hàng" name="customerId">
              {/* <InputForm placeholder="Tìm khách hàng" /> */}
              <LazySelectForm
                placeholder="Tìm khách hàng"
                data={customersInRoute}
                loading={isGetCustomersMutatePending}
                onSelectScrollChange={handleScrollCustomersChange}
              />
            </Form.Item>
          </Col>

          <Col span={5} className="h-[80px]">
            <Form.Item label="Khách hàng cha" name="mainCustomerId">
              <InputForm placeholder="Tìm khách hàng cha" />
            </Form.Item>
          </Col>

          <Col span={5} className="h-[80px]">
            <Form.Item label="Thời gian xuất báo cáo" name="year">
              <DatePicker
                style={{
                  width: '100%',
                }}
                size="large"
                picker="month"
                maxDate={dayjs()}
                placeholder="Chọn thời gian"
                format="MM/YYYY"
                allowClear={false}
              />
            </Form.Item>
          </Col>
        </Row>
      </FormFilter>
    </Content>
  );
};

export default FilterTransactionFrequency;
